import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ToolTip from '../../../Components/ToolTip';
import { Col, Row, Label, Button, Form, Card, CardHeader, CardBody } from 'reactstrap';
import ReportDetailsHeader from '../../../Components/ReportDetailsHeader';
import { TooltipMsg } from '../../../Components/ToolTipMessage';
import AsyncSelect from "react-select/async";
import { ErrorMessage, Field, FieldProps, Formik } from 'formik';
import * as Yup from "yup";
import { customStyle, errorStyle } from '../../../shared/CommonCSS';
import { errorToast, successToast } from '../../../Components/Toasts';
import apiGlobal from '../../../global/api.global';
import { ReportingEventName, ReportingTypes } from '../../../shared/constants';
import { ETADData } from '../../../shared/dataTypes';
import FormValuesDebug from '../../../utils/debugTools/FormValuesDebug';
import { dataTimeFormat, errResponse } from 'GenericForms/Helper';
import '../../../global/GlobalCSS.css';
import { useQuery } from 'react-query';
import { queryKeyes } from 'shared/queryKeys';
import { loadETADObject } from 'VesselMaster/vesselMaster.hooks';
import Loading from 'Components/Loading';
import { queryClient } from 'react-query/queryClient';
import env from 'environment/env';


interface ETADComponentForm {
    record: any,
    lastRecord: any,
    ReportID: number,
    VesselID: number,
    VoyageID: number,
    toggleTab: any,
    activeTab: number,
    etaDataobject: ETADData
}

const ETADComponent = ({
    record,
    lastRecord,
    ReportID,
    VesselID,
    VoyageID,
    toggleTab,
    activeTab,
    etaDataobject
}: ETADComponentForm) => {

    /**
     * Fields availability as per conditions  
     */
    const fields = useMemo(() => {
        const initialFields = {
            arrival_port: false,
            is_arrival_port_in_eca: false,
            expected_time_of_arrival_at_eca: false,
            masters_eta_at_pilot_station: false,
            departure_port: false,
            master_etd_from_present_port: false,
            is_departure_port_in_eca: false,
            expected_time_of_depature_from_eca: false,
            vessel_transit: true,
            date_and_time_of_arrival_at_eca: false,
            date_and_time_of_departure_from_eca: false,

        };

        // ... any additional logic for initializing fields ...

        return initialFields;
    }, []);

    /**
     * State variables for this component
     */
    const [fieldAvailability, setFieldAvailability] = useState(fields);

    //Date conversions 
    if (etaDataobject && etaDataobject?.masters_eta_at_pilot_station) {
        etaDataobject.masters_eta_at_pilot_station = dataTimeFormat(etaDataobject.masters_eta_at_pilot_station)
    }

    //Date conversions 
    if (etaDataobject && etaDataobject?.expected_time_of_arrival_at_eca) {
        etaDataobject.expected_time_of_arrival_at_eca = dataTimeFormat(etaDataobject.expected_time_of_arrival_at_eca)
    }

    /** Queries start */
    /** ETAD object used for edit */
    const { data: ETADObject, isLoading: ETADObjectLoading, isError: ETADObjectError }:
        { data: any, isLoading: any, isError: any } = useQuery(
            [queryKeyes.vessel.ETADObject.key, VesselID, ReportID],
            async () => {
                return await loadETADObject(VesselID, ReportID);
            },
            { staleTime: Infinity }
        )
    /** Queries end */

    /** Assign values to initial object of ETAD */
    const getInitialETAD = () => {
        if (ETADObject?.length > 0 && ETADObject[0].id > 0) {
            return ETADObject[0];
        } else {
            return {
                masters_eta_at_pilot_station: null,
                expected_time_of_arrival_at_eca: null,
                expected_time_of_depature_from_eca: null,
                is_arrival_port_in_eca: false,
                master_etd_from_present_port: null,
                is_departure_port_in_eca: false,
                vessel_transit: false,
                date_and_time_of_arrival_at_eca: null,
                date_and_time_of_departure_from_eca: null,
                arrival_port: null,
                departure_port: null,
                vessel_name: VesselID,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID
            }
        }
    }

    /** UseEffect */
    useEffect(() => {
        ETADFormik.initialValues = { getInitialETAD }
    }, [ETADObject])
    /** UseEffect end */

    const ETADFormik = {
        initialValues: getInitialETAD(),
        validationSchema: Yup.object().shape({
            expected_eta_arrival_at_eca: Yup.date().nullable(),
            expected_eta_departure_eca: Yup.string().nullable(),
            masters_eta_at_pilot_station: Yup.date().nullable(),
            is_arrival_port_in_eca: Yup.boolean(),
            master_etd_from_present_port: Yup.date().nullable(),
            expected_time_of_depature_from_eca: Yup.date().nullable(),
            is_departure_port_in_eca: Yup.boolean(),
            vessel_transit: Yup.boolean(),
            date_and_time_of_arrival_at_eca: Yup.date().nullable(),
            date_and_time_of_departure_from_eca: Yup.date().nullable(),
            arrival_port: Yup.number().nullable(),
            departure_port: Yup.number().nullable(),
            vessel_name: Yup.number(),
            voyage_information: Yup.number(),
            vessel_reporting_information: Yup.number(),
        }),
        onSubmit: (values: any) => {
        }
    }

    const availableFields = useCallback(() => {
        if (record && (
            record?.reporting_type === ReportingTypes.IN_PORT
        )) {
            fields.departure_port = true;
            fields.master_etd_from_present_port = true;
            fields.is_departure_port_in_eca = true;
            fields.expected_time_of_depature_from_eca = true;
        } else if (record && (
            record?.reporting_event_name === ReportingEventName.BEGINING_SEA_PASSAGE
        )) {
            fields.arrival_port = true;
            fields.masters_eta_at_pilot_station = true;
            fields.is_arrival_port_in_eca = true;
            fields.expected_time_of_arrival_at_eca = true;
            fields.departure_port = true;
            fields.date_and_time_of_departure_from_eca = true;
        } else if (record && (
            record?.reporting_type === ReportingTypes.AT_SEA &&
            record?.reporting_event_name !== ReportingEventName.BEGINING_SEA_PASSAGE
        )) {
            fields.arrival_port = true;
            fields.masters_eta_at_pilot_station = true;
            fields.is_arrival_port_in_eca = true;
            fields.expected_time_of_arrival_at_eca = true;
        }

        setFieldAvailability(fields);
    }, [fields, record]);

    useEffect(
        () => {
            availableFields()
        }, [availableFields]
    )

    const searchPorts = async (port: any) => {
        if (port.length >= 3) {
            const response = await apiGlobal.get(`port_master/filter_by_port_name/?port_name=${port}`).then(res => {
                return res.data;
            }).catch(err => {
                if (errResponse.includes(err.response.status)) {
                    errorToast("Internal error occured, please contact the admin");
                }
            })
            return response;
        }
    }

    return (
        <>
            <div className="text-center mb-4">
                <Row>
                    <Col>
                        <h5 className="page_title pos-start mb-0">Expected Time of Arrival/Departure</h5>
                        <p className="card-title-desc pos-start">All readings since last report</p>
                    </Col>
                    <Col>
                        <ReportDetailsHeader />
                    </Col>
                </Row>
            </div>
            {ETADObjectLoading && <Loading message='Loading required data!' />}
            {ETADObjectError && getInitialETAD()}
            {!ETADObjectLoading &&
                <Formik
                    initialValues={ETADFormik.initialValues}
                    validationSchema={ETADFormik.validationSchema}
                    onSubmit={(values: any, actions: { setSubmitting: (arg0: boolean) => void; }) => {
                        actions.setSubmitting(false);
                        const handleResponse = (response: any) => {
                            if (response.status === 201 || response.status === 200) {
                                successToast("Data saved successfully!");
                            }
                        }
                        if (ETADObject && ETADObject[0]?.id) {
                            apiGlobal.put(`expected_time_of_arrival/${values.id}/`, values).then(res => {
                                if (env?.form_validation === true) {
                                    toggleTab(activeTab + 1);
                                }
                                handleResponse(res);
                                queryClient.invalidateQueries(queryKeyes.vessel.ETADObject.key);
                            }).catch(err => {
                                if (errResponse.includes(err.response.status)) {
                                    errorToast("Internal error occured, please contact the admin");
                                }
                            });
                        } else {
                            const res = apiGlobal.post(`expected_time_of_arrival/`, values).then(res => {
                                if (env?.form_validation === true) {
                                    toggleTab(activeTab + 1);
                                }
                                handleResponse(res);
                                queryClient.invalidateQueries(queryKeyes.vessel.ETADObject.key);
                            }).catch(err => {
                                if (errResponse.includes(err.response.status)) {
                                    errorToast("Internal error occured, please contact the admin");
                                }
                            });
                            return res;
                        }

                    }}
                >
                    {({ values, errors, handleSubmit, handleChange, touched }) => (
                        <Form onSubmit={handleSubmit} noValidate autoComplete='off'>
                            <Row>
                                <Col lg={6}>
                                    <Card>
                                        <CardHeader>
                                            <h3 className='card-title'>Arrival</h3>
                                        </CardHeader>
                                        <CardBody className="p-2">
                                            <div>
                                                <div className="mb-3 mt-3">
                                                    <Label className='asteric mb-0'>
                                                        Arrival port name<i className='bx bx-info-circle ml-2p' id='arrival_port_msg' />
                                                    </Label>
                                                    <ToolTip target='arrival_port_msg'
                                                        message={`${TooltipMsg.ETADD.filter((item: any) => item.target === 'arrival_port_msg')
                                                            .map((tool: any) => { return tool.message })}`}
                                                    />
                                                    <Field name="arrival_port">
                                                        {({ field, form }: FieldProps) => (
                                                            <AsyncSelect
                                                                name={field.name}
                                                                cacheOptions
                                                                defaultOptions
                                                                loadOptions={(e: any) => searchPorts(e)}
                                                                getOptionLabel={(e: any) => e.port_name}
                                                                getOptionValue={(e: any) => e.id}
                                                                onChange={(selectedOption: any) => {
                                                                    form.setFieldValue(field.name, selectedOption.id)
                                                                }}
                                                                defaultValue={ETADObject && ETADObject[0]?.id &&
                                                                {
                                                                    id: ETADObject[0]?.arrival_port,
                                                                    port_name: ETADObject[0]?.arrival_port_name
                                                                }}
                                                                menuPortalTarget={document.body}
                                                                styles={errors.arrival_port == null ? customStyle : errorStyle}
                                                                isDisabled={!fieldAvailability.arrival_port}
                                                                noOptionsMessage={() => 'Please enter the first 3 characters of port name'}
                                                            />)}
                                                    </Field>
                                                    <ErrorMessage name="arrival_port">{msg => <div className='error'>{msg}</div>}</ErrorMessage>
                                                </div>
                                                <div className="mb-3">
                                                    <Label className="asteric mb-0">Master's ETA at pilot station(UTC)
                                                        <i className='bx bx-info-circle ml-2p' id='masters_eta_at_pilot_station_msg' />
                                                    </Label>
                                                    <ToolTip target='masters_eta_at_pilot_station_msg'
                                                        message={`${TooltipMsg.ETADD.filter((item: any) => item.target === 'masters_eta_at_pilot_station_msg')
                                                            .map((tool: any) => { return tool.message })}`}
                                                    />
                                                    <Field name="masters_eta_at_pilot_station">
                                                        {({ field, form }: FieldProps) => (
                                                            <input
                                                                type="datetime-local"
                                                                name={field.name}
                                                                min={dataTimeFormat(lastRecord?.reporting_time_utc)}
                                                                max={'9999-12-31T00:00'}
                                                                className='datetimepicker text-uppercase form-control mt-0 w-12'
                                                                defaultValue={dataTimeFormat(values.masters_eta_at_pilot_station, false, true)}
                                                                disabled={!fieldAvailability.masters_eta_at_pilot_station}
                                                                onChange={(e: any) => handleChange(e)}
                                                            />
                                                        )}
                                                    </Field>
                                                    <ErrorMessage name="masters_eta_at_pilot_station">{msg => <Label className='error'>{msg}</Label>}</ErrorMessage>
                                                </div>
                                                <div className='mb-1'>
                                                    <Field name="is_arrival_port_in_eca">
                                                        {({ field, form }: FieldProps) => (
                                                            <input
                                                                className="form-check-input mr-0-5"
                                                                type="checkbox"
                                                                id="is_arrival_port_in_eca"
                                                                name={field.name}
                                                                defaultChecked={values.is_arrival_port_in_eca}
                                                                disabled={!fieldAvailability.is_arrival_port_in_eca}
                                                                onChange={(e: any) => handleChange(e)}
                                                            />
                                                        )}
                                                    </Field>
                                                    <Label>Is arrival port in ECA and vessel is yet to enter the region?
                                                        <i className='bx bx-info-circle ml-2p' id='is_arrival_port_in_eca_msg' />
                                                    </Label>
                                                    <ToolTip target='is_arrival_port_in_eca_msg' message={`${TooltipMsg.ETADD
                                                        .filter((item: any) => item.target === 'is_arrival_port_in_eca_msg')
                                                        .map((tool: any) => { return tool.message })}`}
                                                    />
                                                </div>
                                                <div className="mb-3">
                                                    <Label className='mb-0'>Expected time of arrival at ECA(UTC)
                                                        <i className='bx bx-info-circle ml-2p' id='expected_time_of_arrival_at_eca_msg' />
                                                    </Label>
                                                    <ToolTip target='expected_time_of_arrival_at_eca_msg' message={`${TooltipMsg.ETADD
                                                        .filter((item: any) => item.target === 'expected_time_of_arrival_at_eca_msg')
                                                        .map((tool: any) => { return tool.message })}`}
                                                    />
                                                    <Field name="expected_time_of_arrival_at_eca">
                                                        {({ field, form }: FieldProps) => (
                                                            <input
                                                                type="datetime-local"
                                                                name={field.name}
                                                                min={dataTimeFormat(lastRecord?.reporting_time_utc)}
                                                                max={'9999-12-31T00:00'}
                                                                className='datetimepicker text-uppercase form-control mt-0 w-12'
                                                                defaultValue={dataTimeFormat(values.expected_time_of_arrival_at_eca, false, true)}
                                                                disabled={!values.is_arrival_port_in_eca}
                                                                onChange={(e: any) => handleChange(e)}
                                                            />
                                                        )}
                                                    </Field>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col lg={6}>
                                    <Card>
                                        <CardHeader>
                                            <h3 className='card-title'>Departure</h3>
                                        </CardHeader>
                                        <CardBody className='p-2'>
                                            <div>
                                                <div className="mb-3 mt-3">
                                                    <Label className='asteric mb-0'>
                                                        Departure port name<i className='bx bx-info-circle ml-2p' id='departure_port_msg' />
                                                    </Label>
                                                    <ToolTip target='departure_port_msg' message={`${TooltipMsg.ETADD
                                                        .filter((item: any) => item.target === 'departure_port_msg')
                                                        .map((tool: any) => { return tool.message })}`} 
                                                        />
                                                    <Field name="departure_port">
                                                        {({ field, form }: FieldProps) => (
                                                            <AsyncSelect
                                                                name={field.name}
                                                                cacheOptions
                                                                defaultOptions
                                                                loadOptions={(e: any) => searchPorts(e)}
                                                                getOptionLabel={(e: any) => e?.port_name}
                                                                getOptionValue={(e: any) => e?.id}
                                                                onChange={(selectedOption: any) => { form.setFieldValue(field.name, selectedOption.id) }}
                                                                defaultValue={ETADObject && ETADObject[0]?.id &&
                                                                {
                                                                    id: ETADObject[0]?.departure_port,
                                                                    port_name: ETADObject[0]?.departure_port_name
                                                                }}
                                                                menuPortalTarget={document.body}
                                                                styles={errors.departure_port == null ? customStyle : errorStyle}
                                                                isDisabled={!fieldAvailability.departure_port}
                                                                noOptionsMessage={() => 'Please enter the first 3 characters of port name'}
                                                            />)}
                                                    </Field>
                                                </div>
                                                <div className="mb-3">
                                                    <Label className="asteric mb-0">
                                                        Master's ETD from present port(UTC)
                                                        <i className='bx bx-info-circle ml-2p' id='master_etd_from_present_port_msg' />
                                                    </Label>
                                                    <ToolTip target='master_etd_from_present_port_msg' message={`${TooltipMsg.ETADD
                                                        .filter((item: any) => item.target === 'master_etd_from_present_port_msg')
                                                        .map((tool: any) => { return tool.message })}`}
                                                    />
                                                    <Field name="master_etd_from_present_port">
                                                        {({ field, form }: FieldProps) => (
                                                            <input
                                                                type="datetime-local"
                                                                name={field.name}
                                                                min={dataTimeFormat(lastRecord?.reporting_time_utc)}
                                                                max={'9999-12-31T00:00'}
                                                                className='datetimepicker text-uppercase form-control mt-0 w-12'
                                                                disabled={!fieldAvailability.master_etd_from_present_port}
                                                                defaultValue={dataTimeFormat(values.master_etd_from_present_port, false, true)}
                                                                onChange={(e: any) => handleChange(e)}
                                                            />
                                                        )}
                                                    </Field>
                                                </div>
                                                <div className="mb-1">
                                                    <Field name="is_departure_port_in_eca">
                                                        {({ field, form }: FieldProps) => (
                                                            <input
                                                                className="form-check-input mr-0-5"
                                                                type="checkbox"
                                                                id="is_departure_port_in_eca"
                                                                name={field.name}
                                                                defaultChecked={values.is_departure_port_in_eca}
                                                                onChange={(e: any) => handleChange(e)}
                                                                disabled={!fieldAvailability.is_departure_port_in_eca}
                                                            />
                                                        )}
                                                    </Field>
                                                    <Label>Is departure port in ECA and vessel is yet to exit the region?
                                                        <i className='bx bx-info-circle ml-2p' id='is_departure_port_in_eca_msg' />
                                                    </Label>
                                                    <ToolTip target='is_departure_port_in_eca_msg' message={`${TooltipMsg.ETADD
                                                        .filter((item: any) => item.target === 'is_departure_port_in_eca_msg')
                                                        .map((tool: any) => { return tool.message })}`}
                                                    />
                                                </div>
                                                <div className="mb-3">
                                                    <Label className='mb-0'>Expected time of departure from ECA(UTC)
                                                        <i className='bx bx-info-circle ml-2p' id='expected_time_of_depature_from_eca_msg' />
                                                    </Label>
                                                    <ToolTip target='expected_time_of_depature_from_eca_msg' message={`${TooltipMsg.ETADD
                                                        .filter((item: any) => item.target === 'expected_time_of_depature_from_eca_msg')
                                                        .map((tool: any) => { return tool.message })}`}
                                                    /><Field name="expected_time_of_depature_from_eca">
                                                        {({ field, form }: FieldProps) => (
                                                            <input
                                                                type="datetime-local"
                                                                name={field.name}
                                                                min={dataTimeFormat(lastRecord?.reporting_time_utc)}
                                                                max={'9999-12-31T00:00'}
                                                                className='datetimepicker text-uppercase form-control mt-0 w-12'
                                                                disabled={!values?.is_departure_port_in_eca}
                                                                defaultValue={dataTimeFormat(values.expected_time_of_depature_from_eca, false, true)}
                                                                onChange={(e: any) => handleChange(e)}
                                                            />
                                                        )}
                                                    </Field>
                                                    <ErrorMessage name='expected_time_of_depature_from_eca' component="span" className="error" />
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                            <Row>
                                <Col lg={12}>
                                    <Card>
                                        <CardBody>
                                            <div>
                                                <div className="form-check"><Field name="vessel_transit">
                                                    {({ field, form }: FieldProps) => (
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="vessel_transit"
                                                            name={field.name}
                                                            onChange={handleChange}
                                                            disabled={!fieldAvailability.vessel_transit}
                                                            defaultChecked={values.vessel_transit}
                                                        />
                                                    )}
                                                </Field>
                                                    <Label>Will vessel transit ECA region?
                                                        <i className='bx bx-info-circle ml-2p' id='vessel_transit_msg' />
                                                    </Label>
                                                    <ToolTip target='vessel_transit_msg' message={`${TooltipMsg.ETADD
                                                        .filter((item: any) => item.target === 'vessel_transit_msg')
                                                        .map((tool: any) => { return tool.message })}`}
                                                    />
                                                </div>
                                                {values.vessel_transit &&
                                                    <>
                                                        <div className="mb-3">
                                                            <Label className='mb-0'>Date & time of arrival at ECA incase vessel is yet to enter the region(UTC)
                                                                <i className='bx bx-info-circle ml-2p' id='date_and_time_of_arrival_at_eca_msg' />
                                                            </Label>
                                                            <ToolTip target='date_and_time_of_arrival_at_eca_msg' message={`${TooltipMsg.ETADD
                                                                .filter((item: any) => item.target === 'date_and_time_of_arrival_at_eca_msg')
                                                                .map((tool: any) => { return tool.message })}`}
                                                            />
                                                            <Field name="date_and_time_of_arrival_at_eca">
                                                                {({ field, form }: FieldProps) => (
                                                                    <input
                                                                        type="datetime-local"
                                                                        name={field.name}
                                                                        min={dataTimeFormat(lastRecord.reporting_time_utc)}
                                                                        max={'9999-12-31T00:00'}
                                                                        defaultValue={dataTimeFormat(values.date_and_time_of_arrival_at_eca, false, true)}
                                                                        className='datetimepicker text-uppercase form-control mt-0 w-12'
                                                                        disabled={!values.vessel_transit}
                                                                        onChange={(e: any) => handleChange(e)}
                                                                    />
                                                                )}
                                                            </Field>
                                                            <ErrorMessage name='date_and_time_of_arrival_at_eca' component="span" className="error" />
                                                        </div>
                                                        <div className="mb-2">
                                                            <Label className='mb-0'>Date & time of departure from ECA(UTC)
                                                                <i className='bx bx-info-circle ml-2p' id='date_and_time_of_departure_from_eca_msg' />
                                                            </Label>
                                                            <ToolTip target='date_and_time_of_departure_from_eca_msg' message={`${TooltipMsg.ETADD
                                                                .filter((item: any) => item.target === 'date_and_time_of_departure_from_eca_msg')
                                                                .map((tool: any) => { return tool.message })}`}
                                                            />
                                                            <Field name="date_and_time_of_departure_from_eca">
                                                                {({ field, form }: FieldProps) => (
                                                                    <input
                                                                        type="datetime-local"
                                                                        name={field.name}
                                                                        min={dataTimeFormat(lastRecord.reporting_time_utc)}
                                                                        max={'9999-12-31T00:00'}
                                                                        className='datetimepicker text-uppercase form-control mt-0 w-12'
                                                                        disabled={!values.vessel_transit}
                                                                        defaultValue={dataTimeFormat(values.date_and_time_of_departure_from_eca, false, true)}
                                                                        onChange={(e: any) => handleChange(e)}
                                                                    />
                                                                )}
                                                            </Field>
                                                            <ErrorMessage name='date_and_time_of_departure_from_eca' component="span" className="error" />
                                                        </div>
                                                    </>
                                                }
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                            <Row className="ele_row1">
                                <div className="d-flex flex-wrap gap-5 mt-3 btn-Margin">
                                    <Button type="submit" color="primary" className="btn_size_cstm pos-end" onClick={() => {
                                        if (env?.form_validation === false) {
                                            toggleTab(activeTab + 1);
                                        }
                                    }}>
                                        Next<i className="bx bx-chevron-right ms-1" />
                                    </Button>
                                    <Button type="button" color="primary" className="btn_size_cstm pos-start-0_75" onClick={(e) => {
                                        toggleTab(activeTab - 1);
                                    }}><i className="bx bx-chevron-left me-1" /> Previous</Button>
                                </div>
                            </Row>
                            <FormValuesDebug values={[values, errors, fieldAvailability]} />
                        </Form>
                    )}
                </Formik>
            }
        </>
    )
}

export default ETADComponent;
